import { formatInTimeZone } from "date-fns-tz";

/**
 * Extract date components (year, month, day) from a date field, ignoring timezones.
 * For date-only fields (like summit dates from Airtable), we want to display
 * the literal calendar day/month/year that's in the field, regardless of timezone.
 */
function extractDateComponents(date: Date | string): {
  year: number;
  month: number;
  day: number;
} {
  const d = date instanceof Date ? date : new Date(date);
  // Extract UTC components to get the literal date values
  // This ignores timezone and just gives us the date that's stored
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth(),
    day: d.getUTCDate(),
  };
}

/**
 * Format a date-only field by extracting literal date components, ignoring timezones.
 * For date-only fields (like summit dates from Airtable), we want to display
 * the literal day/month/year that's in the field, not converted to any timezone.
 */
export function formatDateOnly(date: Date | string, formatStr: string): string {
  const { year, month, day } = extractDateComponents(date);
  // Create a date at UTC midnight using the extracted components
  // This ensures we format the literal date, not a timezone-converted one
  const utcDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
  // Format in UTC timezone to ensure the date components are displayed exactly as extracted
  // This completely ignores the user's local timezone
  return formatInTimeZone(utcDate, "UTC", formatStr);
}

/**
 * Check if two dates represent the same calendar day by comparing literal date components.
 */
function isSameDayUTC(date1: Date | string, date2: Date | string): boolean {
  const d1 = extractDateComponents(date1);
  const d2 = extractDateComponents(date2);
  return d1.year === d2.year && d1.month === d2.month && d1.day === d2.day;
}

/**
 * Format a summit date range for display.
 * Uses UTC date components to avoid timezone shifts for date-only fields.
 * Handles same day, same month, same year, and different years.
 *
 * Examples:
 * - Same day: "February 20, 2025"
 * - Same month: "February 20 - 25, 2025"
 * - Same year: "February 20 - March 15, 2025"
 * - Different years: "February 20, 2024 - March 15, 2025"
 */
export function formatSummitDateRange(
  startDate: Date | string,
  endDate: Date | string,
): string {
  const start = extractDateComponents(startDate);
  const end = extractDateComponents(endDate);

  if (isSameDayUTC(startDate, endDate)) {
    return formatDateOnly(startDate, "MMMM d, yyyy");
  }

  if (start.year === end.year && start.month === end.month) {
    return `${formatDateOnly(startDate, "MMMM d")} - ${formatDateOnly(endDate, "d, yyyy")}`;
  }

  if (start.year === end.year) {
    return `${formatDateOnly(startDate, "MMMM d")} - ${formatDateOnly(endDate, "MMMM d, yyyy")}`;
  }

  return `${formatDateOnly(startDate, "MMMM d, yyyy")} - ${formatDateOnly(endDate, "MMMM d, yyyy")}`;
}

/**
 * Format a date-only field for display (e.g., summit day dates).
 * Uses UTC components to avoid timezone shifts.
 */
export function formatSummitDateOnly(
  date: Date | string,
  formatStr: string = "EEEE, MMMM d, yyyy",
): string {
  return formatDateOnly(date, formatStr);
}

/**
 * Normalize a date-only field to a Date object at UTC midnight using literal date components.
 * Use this when you need to pass a Date object to components that expect dates.
 * This extracts the literal year/month/day and creates a UTC date, ignoring timezones.
 */
export function normalizeSummitDate(date: Date | string): Date {
  const { year, month, day } = extractDateComponents(date);
  return new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
}
